import { Card, CardContent, CardHeader, CardTitle, Checkbox, Label, Separator } from '@sunstone/ui';
import { AccessRuleListEditor } from './AccessRuleListEditor';
import { AccessSummary } from './AccessSummary';
import { type RuleDraft, rulesToAccessRuleInputs } from './accessRuleDraft';
import { useTenantName } from './accessRuleSentence';

interface RoleAccessRulesEditorProps {
  tenantId: string;
  /** Set when tenantId is a partner tenant — a partner owns no
   * deployments/workspaces directly, only its child tenants do, so the
   * picker needs to scope by parent_tenant_id instead of tenant_id. */
  parentTenantId?: string;
  roleName: string;
  showRestriction: boolean;
  onShowRestrictionChange: (show: boolean) => void;
  deploymentRules: RuleDraft[];
  onDeploymentRulesChange: (drafts: RuleDraft[]) => void;
  workspaceRules: RuleDraft[];
  onWorkspaceRulesChange: (drafts: RuleDraft[]) => void;
}

/**
 * A role's own access_rules — the envelope every holder's user-level rules
 * can only narrow within, never widen (see accessrule.gateBoth). Reuses
 * AccessRuleListEditor/DeploymentPicker/WorkspacePicker unchanged: none of
 * those are user-specific, and an empty tenantId (a platform-scope role) is
 * already handled by the underlying List RPCs as "platform-wide," so no
 * special-casing is needed here either.
 */
export function RoleAccessRulesEditor({
  tenantId,
  parentTenantId,
  roleName,
  showRestriction,
  onShowRestrictionChange,
  deploymentRules,
  onDeploymentRulesChange,
  workspaceRules,
  onWorkspaceRulesChange,
}: RoleAccessRulesEditorProps) {
  const tenantName = useTenantName(tenantId);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-sm">Restrict to specific resources</CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">
              These rules apply to everyone who holds {roleName || 'this role'}. A person's own
              access can narrow further, but can never reach outside what's allowed here. Optional —
              by default, this role applies across {tenantId ? 'this customer' : 'the platform'}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="role-show-restriction"
              checked={showRestriction}
              onCheckedChange={(checked) => onShowRestrictionChange(checked === true)}
            />
            <Label htmlFor="role-show-restriction" className="text-sm font-normal cursor-pointer">
              Enable
            </Label>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {showRestriction ? (
          <div className="space-y-4">
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Deployments</p>
              <AccessRuleListEditor
                tenantId={tenantId}
                parentTenantId={parentTenantId}
                resourceKind="deployment"
                drafts={deploymentRules}
                onChange={onDeploymentRulesChange}
                allowEffectToggle
              />
            </div>
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-2">Workspaces</p>
              <AccessRuleListEditor
                tenantId={tenantId}
                parentTenantId={parentTenantId}
                resourceKind="workspace"
                drafts={workspaceRules}
                onChange={onWorkspaceRulesChange}
                allowEffectToggle
                roleRules={rulesToAccessRuleInputs(deploymentRules)}
              />
            </div>
          </div>
        ) : null}
        <Separator />
        <AccessSummary
          mode="role-only"
          tenantId={tenantId}
          tenantName={tenantName}
          isPartner={!!parentTenantId}
          deploymentRules={showRestriction ? deploymentRules : []}
          workspaceRules={showRestriction ? workspaceRules : []}
        />
      </CardContent>
    </Card>
  );
}
